import React, { useState, useEffect } from "react";
import {
    FaTimes, FaExclamationTriangle, FaCheckCircle, FaInfoCircle,
    FaFilter, FaCheck, FaBell, FaFire, FaShieldAlt,
    FaServer, FaClock, FaLayerGroup, FaAngleRight, FaRedo,
    FaEye, FaExclamationCircle
} from "react-icons/fa";
import { monitoringApi } from "../../api/monitoring.api";
import "./alerts-panel.css";

// Severity configuration.
const SEVERITY_CONFIG = {
    CRITICAL: {
        color: '#ef4444',
        bg: 'rgba(239,68,68,0.08)',
        gradientFrom: '#ef4444',
        gradientTo: '#dc2626',
        icon: FaFire,
        label: 'Crítico',
        pulse: true,
        textColor: '#991b1b',
    },
    WARNING: {
        color: '#f59e0b',
        bg: 'rgba(245,158,11,0.08)',
        gradientFrom: '#f59e0b',
        gradientTo: '#d97706',
        icon: FaExclamationTriangle,
        label: 'Advertencia',
        pulse: false,
        textColor: '#92400e',
    },
    INFO: {
        color: '#3b82f6',
        bg: 'rgba(59,130,246,0.08)',
        gradientFrom: '#3b82f6',
        gradientTo: '#2563eb',
        icon: FaInfoCircle,
        label: 'Informativo',
        pulse: false,
        textColor: '#1e40af',
    },
};

// Helpers
function timeAgo(isoString) {
    if (!isoString) return '';
    const seconds = Math.floor((Date.now() - new Date(isoString)) / 1000);
    if (seconds < 60) return `hace ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `hace ${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `hace ${hours}h`;
    return new Date(isoString).toLocaleDateString('es-MX', {
        day: '2-digit', month: 'short',
    });
}

function formatFull(isoString) {
    if (!isoString) return '';
    return new Date(isoString).toLocaleString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
}

// Alerts panel component.
function AlertsPanel({ activeAlerts, onClose, onRefresh }) {
    const [filter, setFilter] = useState('ALL');
    const [acknowledging, setAcknowledging] = useState(null);
    const [resolving, setResolving] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Expand the first critical alert on mount.
    useEffect(() => {
        const firstCritical = activeAlerts?.find(a => a.severity === 'CRITICAL');
        if (firstCritical) setExpandedId(firstCritical.id);
    }, []);

    const filtered = filter === 'ALL'
        ? activeAlerts
        : activeAlerts.filter(a => a.severity === filter);

    const counts = {
        ALL: activeAlerts.length,
        CRITICAL: activeAlerts.filter(a => a.severity === 'CRITICAL').length,
        WARNING: activeAlerts.filter(a => a.severity === 'WARNING').length,
        INFO: activeAlerts.filter(a => a.severity === 'INFO').length,
    };

    const handleAcknowledge = async (id) => {
        setAcknowledging(id);
        try {
            await monitoringApi.acknowledgeAlert(id);
            await onRefresh();
        } catch (e) {
            console.error("Error acknowledging alert:", e);
        } finally {
            setAcknowledging(null);
        }
    };

    const handleResolve = async (id) => {
        setResolving(id);
        try {
            await monitoringApi.resolveAlert(id);
            await onRefresh();
        } catch (e) {
            console.error("Error resolving alert:", e);
        } finally {
            setResolving(null);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        try { await onRefresh(); } finally {
            setTimeout(() => setRefreshing(false), 600);
        }
    };

    return (
        <div className="ap-overlay" onClick={onClose}>
            <div className="ap-panel" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="ap-header">
                    <div className="ap-header-left">
                        <div className="ap-header-icon-wrap">
                            <FaBell className="ap-header-icon" />
                            {counts.CRITICAL > 0 && <span className="ap-header-pulse" />}
                        </div>
                        <div>
                            <h2 className="ap-title">Alertas del Sistema</h2>
                            <p className="ap-subtitle">
                                {counts.CRITICAL > 0
                                    ? `${counts.CRITICAL} crítica${counts.CRITICAL > 1 ? 's' : ''} activa${counts.CRITICAL > 1 ? 's' : ''}`
                                    : `${counts.ALL} alerta${counts.ALL !== 1 ? 's' : ''} activa${counts.ALL !== 1 ? 's' : ''}`}
                            </p>
                        </div>
                    </div>
                    <div className="ap-header-right">
                        <button
                            className={`ap-icon-btn ${refreshing ? 'spinning' : ''}`}
                            onClick={handleRefresh}
                            title="Actualizar"
                        >
                            <FaRedo />
                        </button>
                        <button className="ap-icon-btn" onClick={onClose} title="Cerrar">
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Stats bar */}
                <div className="ap-stats">
                    {[
                        { key: 'CRITICAL', label: 'Críticas', color: '#ef4444', icon: FaFire },
                        { key: 'WARNING',  label: 'Advertencias', color: '#f59e0b', icon: FaExclamationTriangle },
                        { key: 'INFO',     label: 'Informativas', color: '#3b82f6', icon: FaInfoCircle },
                    ].map(({ key, label, color, icon: Icon }) => (
                        <button
                            key={key}
                            className={`ap-stat-chip ${filter === key ? 'active' : ''}`}
                            style={{ '--chip-color': color }}
                            onClick={() => setFilter(filter === key ? 'ALL' : key)}
                        >
                            <Icon className="ap-stat-icon" />
                            <span className="ap-stat-count">{counts[key]}</span>
                            <span className="ap-stat-label">{label}</span>
                        </button>
                    ))}
                </div>

                {/* Filter bar */}
                <div className="ap-filter-bar">
                    <FaFilter className="ap-filter-icon" />
                    {['ALL', 'CRITICAL', 'WARNING', 'INFO'].map(s => (
                        <button
                            key={s}
                            className={`ap-filter-btn ${filter === s ? 'active' : ''}`}
                            data-severity={s}
                            onClick={() => setFilter(s)}
                        >
                            {s === 'ALL' ? `Todas (${counts.ALL})` : `${SEVERITY_CONFIG[s]?.label} (${counts[s]})`}
                        </button>
                    ))}
                </div>

                {/* Alert list. */}
                <div className="ap-list">
                    {filtered.length === 0 ? (
                        <div className="ap-empty">
                            <FaShieldAlt className="ap-empty-icon" />
                            <p className="ap-empty-title">Sin alertas</p>
                            <p className="ap-empty-sub">
                                {filter !== 'ALL'
                                    ? `No hay alertas de tipo "${SEVERITY_CONFIG[filter]?.label}"`
                                    : 'El sistema opera normalmente.'}
                            </p>
                        </div>
                    ) : (
                        filtered.map(alert => {
                            const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.INFO;
                            const Icon = cfg.icon;
                            const isExpanded = expandedId === alert.id;
                            const isAcking = acknowledging === alert.id;
                            const isResolving = resolving === alert.id;

                            return (
                                <div
                                    key={alert.id}
                                    className={`ap-item ap-item--${alert.severity?.toLowerCase()} ${isExpanded ? 'expanded' : ''}`}
                                >
                                    {/* Side severity indicator. */}
                                    <div
                                        className="ap-item-stripe"
                                        style={{ background: `linear-gradient(180deg, ${cfg.gradientFrom}, ${cfg.gradientTo})` }}
                                    >
                                        {cfg.pulse && <span className="ap-stripe-pulse" />}
                                    </div>

                                    <div className="ap-item-body">
                                        {/* Alert item header. */}
                                        <div
                                            className="ap-item-header"
                                            onClick={() => setExpandedId(isExpanded ? null : alert.id)}
                                        >
                                            <div className="ap-item-left">
                                                <span
                                                    className="ap-severity-badge"
                                                    style={{ background: cfg.bg, color: cfg.color }}
                                                >
                                                    <Icon />
                                                    {cfg.label}
                                                </span>
                                                <span className="ap-item-name">{alert.ruleName}</span>
                                            </div>
                                            <div className="ap-item-right">
                                                <span className="ap-item-time" title={formatFull(alert.triggeredAt)}>
                                                    <FaClock />
                                                    {timeAgo(alert.triggeredAt)}
                                                </span>
                                                <FaAngleRight
                                                    className={`ap-expand-icon ${isExpanded ? 'rotated' : ''}`}
                                                />
                                            </div>
                                        </div>

                                        {/* Summary message. */}
                                        <p className="ap-item-message">{alert.message}</p>

                                        {/* Module/service tags. */}
                                        {(alert.module || alert.service) && (
                                            <div className="ap-item-tags">
                                                {alert.module && (
                                                    <span className="ap-tag">
                                                        <FaLayerGroup /> {alert.module}
                                                    </span>
                                                )}
                                                {alert.service && (
                                                    <span className="ap-tag">
                                                        <FaServer /> {alert.service}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Expandable details. */}
                                        {isExpanded && (
                                            <div className="ap-item-detail">
                                                <div className="ap-detail-section">
                                                    <p className="ap-detail-label">
                                                        <FaExclamationCircle /> Descripción técnica
                                                    </p>
                                                    <p className="ap-detail-text">
                                                        {alert.description || alert.message}
                                                    </p>
                                                </div>

                                                {alert.metric && (
                                                    <div className="ap-detail-section">
                                                        <p className="ap-detail-label">
                                                            <FaEye /> Métrica detectada
                                                        </p>
                                                        <p className="ap-detail-text ap-metric-val">
                                                            {alert.metric}: <strong>{alert.currentValue}</strong>
                                                            {alert.threshold && (
                                                                <span className="ap-threshold">
                                                                    (umbral: {alert.threshold})
                                                                </span>
                                                            )}
                                                        </p>
                                                    </div>
                                                )}

                                                <div className="ap-detail-meta-grid">
                                                    {alert.triggeredAt && (
                                                        <div className="ap-meta-item">
                                                            <span className="ap-meta-key">Activada</span>
                                                            <span className="ap-meta-val">{formatFull(alert.triggeredAt)}</span>
                                                        </div>
                                                    )}
                                                    {alert.id && (
                                                        <div className="ap-meta-item">
                                                            <span className="ap-meta-key">ID Alerta</span>
                                                            <span className="ap-meta-val ap-mono">#{alert.id}</span>
                                                        </div>
                                                    )}
                                                    {alert.status && (
                                                        <div className="ap-meta-item">
                                                            <span className="ap-meta-key">Estado</span>
                                                            <span className="ap-meta-val">{alert.status}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Suggested action. */}
                                                <div className={`ap-suggestion ap-suggestion--${alert.severity?.toLowerCase()}`}>
                                                    <span className="ap-suggestion-label">💡 Acción recomendada:</span>
                                                    {alert.severity === 'CRITICAL'
                                                        ? `Intervención inmediata requerida. Revisa el módulo ${alert.module || 'afectado'} y consulta los logs de error recientes.`
                                                        : alert.severity === 'WARNING'
                                                        ? `Monitorea el comportamiento. Si persiste, escala a soporte técnico.`
                                                        : `Registro informativo. No se requiere acción inmediata.`}
                                                </div>
                                            </div>
                                        )}

                                        {/* Actions. */}
                                        <div className="ap-item-actions">
                                            <button
                                                className="ap-btn ap-btn--ack"
                                                onClick={() => handleAcknowledge(alert.id)}
                                                disabled={isAcking || isResolving}
                                            >
                                                <FaCheck />
                                                {isAcking ? 'Reconociendo...' : 'Reconocer'}
                                            </button>
                                            <button
                                                className="ap-btn ap-btn--resolve"
                                                onClick={() => handleResolve(alert.id)}
                                                disabled={isResolving || isAcking}
                                            >
                                                <FaCheckCircle />
                                                {isResolving ? 'Resolviendo...' : 'Resolver'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="ap-footer">
                    <span className="ap-footer-text">
                        {filtered.length} de {counts.ALL} alertas mostradas
                    </span>
                    <button className="ap-footer-btn" onClick={onClose}>
                        Cerrar panel
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AlertsPanel;
